import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Button, Collapse, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, OrderedList, ListItem, Switch, FormLabel, useToast,
} from "@chakra-ui/react";
import { readCache, writeCache, formatAge, DB_HEALTH_KEY, DB_HEALTH_LIVE_KEY } from "../../../utils/resultCache";
import AssignModal from "../../../components/messaging/AssignModal";
import { SourceTaskChip, useSourceTasks } from "./issueTasks";
import UsageMeters, { usageText } from "./UsageMeters";
import useLiveRefresh, { LIVE_EVERY_MS } from "./useLiveRefresh";
import { ImpactChip, ImpactDonut, RiskDonut } from "./StatusDonut";
import { AiFindingCard, AiFindingsNote, SizingNote, aiFindingTaskText, useCodeFindings, useSizing } from "./AiFindings";
import {
  byImpact, checkImpact, chipText, dbCheckEffect, effectOf, findingEffect, findingImpact, pctOf, splitHealthAndRisk,
  stateOf, withEffect, worseOf, NOW,
} from "./impact";
import { useLastNight, pickNewer, LastNightLabel } from "./LastNight";

/**
 * The DB Health tab, in Settings and as the third tab of the Live Activity
 * modal: a read-only scan of this backend's database for what usually takes an
 * app down (the project paused, connections or memory running out, stuck
 * queries, a clinic the API can't reach), each answered in plain English with
 * what it means and what to do. The backend writes the wording
 * (api/db_health/checks.py); this panel only lays it out, worst first. The
 * last report is kept in localStorage like the other tabs.
 *
 * Above the checks: gauges of how close the database is to its limits, and,
 * whenever something is failing, the verdict on Supabase itself (is it the
 * cause, or only where the symptoms show?). When Supabase is so far down that
 * the backend can't confirm who is asking, the report is Supabase's own health
 * alone (report.outage_reason says why).
 *
 * Live (on unless switched off, remembered per browser): while the panel is in
 * view it asks for a fresh report every 15 seconds, so the verdict, the gauges
 * and Supabase's health stay current, and a change for the worse (or back to
 * healthy) raises a toast. The backend keeps those refreshes cheap: only the
 * quick facts are re-read, and the slow checks (logs, slow queries, clinics,
 * tables, backups) are repeated every few minutes (report.full_ran_at).
 *
 * Any check that isn't OK can be assigned as a Messages task, linked back by
 * the check id (source_kind "db_health"); the row then shows its task, even
 * after the check passes, so whoever assigned it can confirm the fix.
 *
 * The server also runs the check by itself each night that had commits; that report shows here,
 * labelled, when it's newer than the last one run from this browser. Below the checks: the
 * database problems the nightly AI review found in the day's commits (area "database").
 *
 * How bad each one is: every check carries an impact, the share of requests, clinics' users or
 * patient data it affects (api/db_health/checks.py), and the AI findings carry the review's own
 * estimate. The verdict and the donut are the tab's health from those (impact.js), so a few small
 * warnings don't paint the tab red.
 */

const STATUS = {
  problem: { label: "PROBLEM", scheme: "red", order: 0 },
  warning: { label: "WARNING", scheme: "orange", order: 1 },
  unknown: { label: "COULDN'T CHECK", scheme: "gray", order: 2 },
  ok: { label: "OK", scheme: "green", order: 3 },
};

const VERDICT = {
  problem: { scheme: "red", label: "ACTION NEEDED" },
  warning: { scheme: "orange", label: "KEEP AN EYE ON" },
  unknown: { scheme: "gray", label: "INCOMPLETE" },
  ok: { scheme: "green", label: "HEALTHY" },
};
// The verdict for each state of the tab's health (impact.js).
const VERDICT_OF_STATE = { urgent: "problem", minor: "warning", good: "ok" };
// What a check's impact is a share of, when it was saved before checks said.
const CHECK_OF = "requests";

/** A check's impact, saying whether it's failing now (a problem) or one to keep an eye on (a warning). */
export function dbImpact(check) {
  return withEffect(checkImpact(check, CHECK_OF), dbCheckEffect(check?.status));
}

/** A nightly review finding's impact: a risk, not something failing now. */
function aiImpact(finding) {
  return withEffect(findingImpact(finding), findingEffect(finding));
}

/** The verdict's headline, from the same list as below it: how many, how many fail now, the biggest. */
export function verdictHeadline(report, attention) {
  if (!attention.length) return report?.headline || "";
  const failing = attention.filter((r) => effectOf(r.impact) === NOW).length;
  const biggest = attention[0];
  const size = pctOf(biggest.impact) ? ` (${chipText(biggest.impact)})` : "";
  const lead = failing ? `${failing} failing now` : "No outage-level problems";
  return `${lead} · ${attention.length} to fix, the biggest: ${biggest.item.title}${size}`;
}

const WORSE = { ok: 0, unknown: 1, warning: 2, problem: 3 };

// The scan in progress, per run function. It lives outside the component so leaving
// the tab or closing the modal mid-scan doesn't lose it: the panel picks it back up
// when it mounts again, and the result is cached whether or not anyone is watching.
const inFlight = new WeakMap();

function startScan(run) {
  let scan = inFlight.get(run);
  if (!scan) {
    scan = Promise.resolve()
      .then(() => run())
      .then((result) => {
        writeCache(DB_HEALTH_KEY, result);
        return result;
      })
      .finally(() => inFlight.delete(run));
    inFlight.set(run, scan);
  }
  return scan;
}

function byWorstFirst(checks) {
  return [...(checks || [])].sort((a, b) => (STATUS[a.status]?.order ?? 9) - (STATUS[b.status]?.order ?? 9));
}

function projectLabel(project) {
  if (!project) return "";
  const name = project.name || project.ref || "";
  return project.region ? `${name} (${project.region})` : name;
}

// The verdict on Supabase itself is the next question once anything is failing; while
// everything passes, the checks already say it.
function showsSupabase(report) {
  return Boolean(report?.supabase) && (report.overall !== "ok" || report.supabase.status !== "ok");
}

/** Plain-text version of a report, for pasting into a message or ticket. */
export function summaryText(report) {
  const checks = byWorstFirst(report.checks);
  const when = report.ran_at ? new Date(report.ran_at).toLocaleString() : "";
  const lines = [`DB health check: ${projectLabel(report.project)}${when ? `, ${when}` : ""}`, report.headline];
  if (report.outage_reason) lines.push(report.outage_reason);
  if (report.usage?.length) lines.push(`Usage: ${usageText(report.usage)}.`);
  if (showsSupabase(report)) lines.push(`Supabase: ${report.supabase.headline} ${report.supabase.detail || ""}`.trim());
  for (const c of checks.filter((x) => x.status !== "ok")) {
    lines.push("", `${STATUS[c.status]?.label || c.status}: ${c.title}. ${c.headline}`);
    if (c.explain) lines.push(c.explain);
    (c.fix || []).forEach((step, i) => lines.push(`  ${i + 1}. ${step}`));
  }
  if (report.setup_note) lines.push("", report.setup_note);
  const passed = checks.filter((x) => x.status === "ok").map((x) => x.title);
  if (passed.length) lines.push("", `Passed: ${passed.join(", ")}.`);
  return lines.join("\n");
}

// A task gets at most this many rows of a check's table; the rest stay on this tab.
const TASK_TABLE_ROWS = 8;

/** Title and description for a task made from one check: what was found and what to
 * do, like the summary, plus the numbers for whoever fixes it. */
export function checkTaskText(check, report) {
  const lines = [`${(STATUS[check.status] || STATUS.unknown).label}: ${check.headline}`];
  if (check.explain) lines.push(`What this means: ${check.explain}`);
  if (check.fix?.length) {
    lines.push("What to do:");
    check.fix.forEach((step, i) => lines.push(`  ${i + 1}. ${step}`));
  }
  const table = check.table;
  const rows = table?.rows || [];
  const numbers = [
    ...(check.facts || []).map((f) => `  ${f.label}: ${f.value}`),
    ...rows.slice(0, TASK_TABLE_ROWS).map((row) => `  ${row
      .map((cell, j) => (table.columns?.[j] ? `${table.columns[j]}: ${cell}` : String(cell)))
      .join(" · ")}`),
  ];
  if (numbers.length) lines.push("Numbers:", ...numbers);
  if (rows.length > TASK_TABLE_ROWS) {
    lines.push(`  …and ${rows.length - TASK_TABLE_ROWS} more in SuperAdmin → Live Activity → DB Health Check`);
  }
  if (check.owners?.length) lines.push(`Suggested to fix: ${check.owners.join(", ")}`);
  const project = projectLabel(report?.project);
  const when = report?.ran_at ? new Date(report.ran_at).toLocaleString() : "";
  lines.push(`Found by the DB health check${project ? ` of ${project}` : ""}${when ? ` on ${when}` : ""} · check id ${check.id}`);
  return { title: `DB health: ${check.title}`, description: lines.join("\n") };
}

function Numbers({ check }) {
  const facts = check.facts || [];
  const table = check.table;
  return (
    <Box mt={2} p={2} bg="gray.50" borderRadius="md" fontSize="xs" data-testid={`db-health-numbers-${check.id}`}>
      {facts.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={4} spacingY={1} mb={table?.rows?.length ? 2 : 0}>
          {facts.map((f) => (
            <HStack key={f.label} align="baseline" spacing={2}>
              <Text color="gray.600">{f.label}:</Text>
              <Text fontWeight="semibold">{f.value}</Text>
            </HStack>
          ))}
        </SimpleGrid>
      )}
      {table?.rows?.length > 0 && (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>{table.columns.map((col) => <Th key={col} fontSize="2xs" px={2}>{col}</Th>)}</Tr>
            </Thead>
            <Tbody>
              {table.rows.map((row, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Tr key={i}>
                  {row.map((cell, j) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Td key={j} fontSize="xs" px={2} fontFamily={table.columns[j] === "Query" ? "mono" : undefined}
                        maxW={table.columns[j] === "Query" ? "420px" : undefined} whiteSpace="normal" wordBreak="break-word">
                      {String(cell)}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

function NumbersToggle({ check, open, onToggle }) {
  if (!(check.facts?.length || check.table?.rows?.length)) return null;
  return (
    <Button size="xs" variant="link" colorScheme="blue" onClick={onToggle} data-testid={`db-health-toggle-${check.id}`}>
      {open ? "Hide the numbers" : "Show the numbers"}
    </Button>
  );
}

function AssignButton({ check, canAssign, onAssign }) {
  return (
    <Button size="xs" variant="outline" flexShrink={0} data-testid={`db-health-assign-${check.id}`}
            isDisabled={!canAssign} onClick={() => onAssign(check)}>
      Assign task
    </Button>
  );
}

// Who to ask to fix a check that isn't OK, from git history (api/db_health/checks.py
// SUGGESTED_OWNERS), the way the Memory Issues tab suggests who should fix a failing issue.
function Owners({ check }) {
  if (check.status === "ok") return null;
  if (!Array.isArray(check.owners)) {
    // A report saved in this browser before checks named anyone: only a new scan has the names.
    return (
      <Text fontSize="xs" color="gray.500" data-testid={`db-health-owners-${check.id}`}>
        Run the check again to see who to ask about this.
      </Text>
    );
  }
  if (!check.owners.length) return null;
  return (
    <Text fontSize="xs" color="red.700" data-testid={`db-health-owners-${check.id}`}
          title="Ranked by who wrote the code this check depends on (git history)">
      Suggested to fix: {check.owners.join(", ")}
    </Text>
  );
}

function TaskChip({ check, task }) {
  return task ? <SourceTaskChip task={task} testId={`db-health-task-${check.id}`} /> : null;
}

function AttentionCard({ check, task, canAssign, onAssign }) {
  const meta = STATUS[check.status] || STATUS.unknown;
  const [open, setOpen] = useState(false);
  return (
    <Box borderWidth="1px" borderLeftWidth="4px" borderLeftColor={`${meta.scheme}.400`} borderRadius="md" p={3}
         data-testid={`db-health-check-${check.id}`} data-status={check.status}>
      <HStack justify="space-between" align="flex-start" spacing={2} mb={1}>
        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={meta.scheme}>{meta.label}</Badge>
          <Text fontWeight="bold">{check.title}</Text>
          <ImpactChip impact={dbImpact(check)} testId={`db-health-impact-${check.id}`} />
          {check.group_label && <Text fontSize="xs" color="gray.500">{check.group_label}</Text>}
        </HStack>
        <AssignButton check={check} canAssign={canAssign} onAssign={onAssign} />
      </HStack>
      <Text fontSize="md" fontWeight="semibold" mb={check.owners?.length ? 0.5 : 2}>{check.headline}</Text>
      <Box mb={2}><Owners check={check} /></Box>
      {task && <Box mt={-1} mb={2}><TaskChip check={check} task={task} /></Box>}
      {check.explain && (
        <Box mb={2}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">WHAT THIS MEANS</Text>
          <Text fontSize="sm">{check.explain}</Text>
        </Box>
      )}
      {check.fix?.length > 0 && (
        <Box mb={1}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">WHAT TO DO</Text>
          <OrderedList fontSize="sm" spacing={1} ml={5}>
            {check.fix.map((step) => <ListItem key={step}>{step}</ListItem>)}
          </OrderedList>
        </Box>
      )}
      <NumbersToggle check={check} open={open} onToggle={() => setOpen((o) => !o)} />
      <Collapse in={open} animateOpacity unmountOnExit><Numbers check={check} /></Collapse>
    </Box>
  );
}

/**
 * The backend's email watcher (api/db_health/watch.py): whether it runs, who it
 * emails and from where, its last look and its last email. It works with no tab
 * open; this line only shows what it is doing.
 */
export function EmailAlerts({ alerts }) {
  if (!alerts) return null;
  if (!alerts.running) {
    return (
      <Alert status="warning" borderRadius="md" data-testid="db-health-alerts" data-state="off">
        <AlertIcon />
        <Text fontSize="sm"><b>Email alerts are off.</b> {alerts.reason}</Text>
      </Alert>
    );
  }
  const last = alerts.last_email;
  const parts = [
    `to ${(alerts.recipients || []).join(", ")}`,
    alerts.sender ? `from ${alerts.sender}` : `from the app's ${alerts.transport || "email"} account`,
    alerts.checked_at ? `last checked ${formatAge(alerts.checked_at)}` : "first check under way",
  ];
  return (
    <Box fontSize="sm" px={3} py={2} borderRadius="md" borderWidth="1px" data-testid="db-health-alerts"
         data-state={alerts.alerting ? "alerting" : "on"}
         bg={alerts.alerting ? "red.50" : undefined} borderColor={alerts.alerting ? "red.300" : "gray.200"}>
      <HStack spacing={2} flexWrap="wrap">
        <Badge colorScheme={alerts.alerting ? "red" : "green"}>{alerts.alerting ? "ALERTING" : "EMAIL ALERTS ON"}</Badge>
        <Text>{parts.join(" · ")}</Text>
      </HStack>
      {alerts.problems?.length > 0 && !alerts.alerting && (
        <Text mt={1} color="orange.700">
          Found a problem ({alerts.problems.join(", ")}); checking again in {alerts.confirm_after_s} s before emailing.
        </Text>
      )}
      {last && (
        <Text mt={1} color="gray.700" data-testid="db-health-alerts-last">
          Last email {formatAge(last.at)}: {last.subject}
        </Text>
      )}
      {alerts.last_error && (
        <Text mt={1} color="red.600" data-testid="db-health-alerts-error">{alerts.last_error.message}</Text>
      )}
    </Box>
  );
}

function SupabaseHealth({ supabase }) {
  const meta = STATUS[supabase.status] || STATUS.unknown;
  return (
    <Box borderWidth="1px" borderLeftWidth="4px" borderLeftColor={`${meta.scheme}.400`} borderRadius="md" p={3}
         data-testid="db-health-supabase" data-status={supabase.status}>
      <HStack spacing={2} flexWrap="wrap" mb={1}>
        <Badge colorScheme={meta.scheme}>{meta.label}</Badge>
        <Text fontSize="xs" fontWeight="bold" color="gray.600">SUPABASE HEALTH</Text>
      </HStack>
      <Text fontWeight="semibold">{supabase.headline}</Text>
      {supabase.detail && <Text fontSize="sm">{supabase.detail}</Text>}
    </Box>
  );
}

// A passed check has nothing to assign, but keeps showing a task it already has.
function CompactRow({ check, task, canAssign, onAssign }) {
  const meta = STATUS[check.status] || STATUS.unknown;
  const [open, setOpen] = useState(false);
  return (
    <Box py={2} borderBottomWidth="1px" borderColor="gray.100" data-testid={`db-health-check-${check.id}`}
         data-status={check.status}>
      <HStack justify="space-between" align="flex-start" spacing={2}>
        <HStack align="baseline" spacing={2} flexWrap="wrap" minW={0}>
          <Badge colorScheme={meta.scheme} variant="subtle">{meta.label}</Badge>
          <Text fontWeight="semibold" fontSize="sm">{check.title}</Text>
          <Text fontSize="sm" color="gray.700">{check.headline}</Text>
          <NumbersToggle check={check} open={open} onToggle={() => setOpen((o) => !o)} />
        </HStack>
        {check.status !== "ok" && <AssignButton check={check} canAssign={canAssign} onAssign={onAssign} />}
      </HStack>
      <Owners check={check} />
      <TaskChip check={check} task={task} />
      <Collapse in={open} animateOpacity unmountOnExit><Numbers check={check} /></Collapse>
    </Box>
  );
}

export default function DbHealthPanel({ run, loadFindings, dismissFinding, loadLastNight, startSizing, loadSizing,
                                       sizingPollMs }) {
  const [entry, setEntry] = useState(() => readCache(DB_HEALTH_KEY));
  const lastNight = useLastNight("db_health", loadLastNight);
  const [assigningAi, setAssigningAi] = useState(null); // an AI finding being assigned
  const [running, setRunning] = useState(() => inFlight.has(run));
  const [error, setError] = useState(null);
  const [live, setLive] = useState(() => readCache(DB_HEALTH_LIVE_KEY)?.data !== false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const rootRef = useRef(null);
  const [assigning, setAssigning] = useState(null); // the check being assigned
  const { me, taskByRef, reloadTasks } = useSourceTasks("db_health", "DB health check");
  // The open database findings of the nightly AI review: listed below the checks, and part of the health.
  const found = useCodeFindings("database", loadFindings, dismissFinding);
  // A manual run also sizes the findings saved without an impact (one sizing covers every tab).
  const { sizing, sizeNow } = useSizing(found, startSizing, loadSizing, sizingPollMs);
  const toast = useToast();
  const mounted = useRef(true);
  const shown = pickNewer(entry, lastNight.run);
  const report = shown?.data || null;

  const follow = useCallback((scan) => {
    setRunning(true);
    setError(null);
    scan
      .then((result) => {
        if (mounted.current) setEntry({ data: result, savedAt: new Date().toISOString() });
      })
      .catch((e) => {
        if (mounted.current) setError(e?.message || String(e));
      })
      .finally(() => {
        if (mounted.current) setRunning(false);
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    const scan = inFlight.get(run);
    if (scan) follow(scan);
    return () => {
      mounted.current = false;
    };
  }, [run, follow]);

  const runCheck = useCallback(() => {
    follow(startScan(run));
    sizeNow();
  }, [run, follow, sizeNow]);

  const refreshLive = useCallback(async () => {
    if (inFlight.has(run)) return;   // a pressed scan is under way and will bring the news
    setRefreshing(true);
    try {
      const result = await run({ live: true });
      writeCache(DB_HEALTH_KEY, result);
      if (!mounted.current) return;
      // A refresh that left before a pressed scan can land after it; the newer report stays.
      setEntry((prev) => (prev?.data?.ran_at > result.ran_at ? prev : { data: result, savedAt: new Date().toISOString() }));
      setLiveError(null);
      setError(null);
    } catch (e) {
      // 409: a scan is already running on the backend; the next refresh picks up its result.
      if (mounted.current && e?.status !== 409) setLiveError(e?.message || String(e));
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, [run]);
  useLiveRefresh({ enabled: live, rootRef, refresh: refreshLive });

  const toggleLive = useCallback((e) => {
    const on = e.target.checked;
    writeCache(DB_HEALTH_LIVE_KEY, on);
    setLive(on);
    if (!on) setLiveError(null);
  }, []);

  // While live, a verdict that gets worse, or comes back to healthy, is announced.
  const overall = report?.overall;
  const lastOverall = useRef(overall);
  useEffect(() => {
    const was = lastOverall.current;
    lastOverall.current = overall;
    if (!live || !was || !overall || was === overall) return;
    const label = (VERDICT[overall] || VERDICT.unknown).label;
    if (WORSE[overall] > WORSE[was]) {
      toast({ title: `Database health: ${label}`, description: report.headline, duration: 10000, isClosable: true,
              status: overall === "problem" ? "error" : "warning" });
    } else if (overall === "ok") {
      toast({ title: "Database health is back to healthy", status: "success", duration: 6000, isClosable: true });
    }
  }, [overall, live, report, toast]);

  const copySummary = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(summaryText(report));
      toast({ title: "Summary copied", status: "success", duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: "Couldn't copy the summary", description: e?.message, status: "error", duration: 3000, isClosable: true });
    }
  }, [report, toast]);

  const checks = byWorstFirst(report?.checks);
  // One list of what to fix, most important first: the problems and warnings the checks found and the
  // nightly code review's open database findings, by how much each affects (the worse status first
  // when two are the same size; findings not sized yet after the ones that are).
  const attention = byImpact([
    ...checks.filter((c) => c.status === "problem" || c.status === "warning")
      .map((c) => ({ kind: "check", id: c.id, item: c, impact: dbImpact(c), worse: worseOf(c.status) })),
    ...(found.findings || []).map((f) => ({ kind: "ai", id: f.id, item: f, impact: aiImpact(f),
                                            worse: worseOf(f.severity) })),
  ], (r) => pctOf(r.impact), (r) => r.worse);
  const unknown = checks.filter((c) => c.status === "unknown");
  const passed = checks.filter((c) => c.status === "ok");
  const impactItems = [
    ...checks.map((c) => ({ key: c.id, label: c.title, impact: dbImpact(c) })),
    ...(found.findings || []).map((f) => ({ key: f.id, label: f.title, impact: aiImpact(f) })),
  ];
  const state = stateOf(impactItems);
  const rings = splitHealthAndRisk(impactItems);
  const anyKnown = impactItems.some((i) => pctOf(i) !== null);
  // Nothing measured, or nothing wrong in what ran while some checks couldn't run: incomplete, not healthy.
  const verdictKey = !anyKnown || (state === "good" && report?.overall === "unknown") ? "unknown"
    : VERDICT_OF_STATE[state];
  const verdict = VERDICT[verdictKey];
  const rowTask = (check) => ({ task: taskByRef[check.id], canAssign: Boolean(me), onAssign: setAssigning });

  return (
    <VStack align="stretch" spacing={4} ref={rootRef}>
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Check this backend's database for what usually causes an outage</Text>
          <Text fontSize="sm">
            Whether it's up, what went wrong in the last 24 hours, how close it is to its limits, anything stuck,
            whether every clinic can reach it, and whether the code and the database still match.
            If anything is failing, it also says whether Supabase itself is the cause.
            With Live on, it refreshes every {LIVE_EVERY_MS / 1000} seconds while this tab is in view.
            It only reads: nothing is changed. The server also runs it by itself each night that had commits.
          </Text>
        </Box>
      </Alert>

      <HStack justify="space-between" flexWrap="wrap" spacing={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Button colorScheme="blue" size="sm" onClick={runCheck} isLoading={running} loadingText="Checking…"
                  data-testid="db-health-run">
            {report ? "Run again" : "Run health check"}
          </Button>
          {report && shown.source === "nightly" && <LastNightLabel run={shown.run} testId="db-health-last-night" />}
          {report && shown.source === "live" && (
            <Text fontSize="xs" color="gray.600" data-testid="db-health-meta">
              {report.live ? "Updated" : "Ran"} {formatAge(entry?.savedAt)} · {projectLabel(report.project)}
              {report.live
                ? ` · logs, slow queries, clinics, tables and backups as of ${formatAge(report.full_ran_at)}`
                : report.duration_ms != null ? ` · took ${(report.duration_ms / 1000).toFixed(1)} s` : ""}
            </Text>
          )}
        </HStack>
        <HStack spacing={3} flexWrap="wrap">
          <HStack spacing={2}>
            <Switch id="db-health-live-switch" size="sm" colorScheme="green" isChecked={live} onChange={toggleLive}
                    data-testid="db-health-live-switch" />
            <FormLabel htmlFor="db-health-live-switch" mb={0} mr={0} fontSize="sm">Live</FormLabel>
            {live && (
              <Badge colorScheme={liveError ? "orange" : "green"} data-testid="db-health-live-state">
                {refreshing ? "Updating…" : liveError ? "Retrying" : `Every ${LIVE_EVERY_MS / 1000} s`}
              </Badge>
            )}
          </HStack>
          {report && (
            <Button size="sm" variant="outline" onClick={copySummary} data-testid="db-health-copy">
              Copy summary
            </Button>
          )}
        </HStack>
      </HStack>

      {live && liveError && (
        <Alert status="warning" borderRadius="md" data-testid="db-health-live-error">
          <AlertIcon />
          Couldn't refresh: {liveError}{report ? " Showing the last result." : ""} Trying again every {LIVE_EVERY_MS / 1000} seconds.
        </Alert>
      )}

      {error && (
        <Alert status="error" borderRadius="md" data-testid="db-health-error">
          <AlertIcon />Couldn't run the health check: {error}
        </Alert>
      )}

      {lastNight.run?.status === "failed" && shown?.source !== "nightly" && (
        <LastNightLabel run={lastNight.run} testId="db-health-last-night" />
      )}
      {!report && !running && !error && !live && (
        <Text fontSize="sm" color="gray.600">
          No check has been run from this browser yet, and the server hasn't run one at night yet. Run one: it takes
          about ten seconds.
        </Text>
      )}
      {!report && live && refreshing && (
        <Text fontSize="sm" color="gray.600">Running the first check: it takes about ten seconds.</Text>
      )}

      {report && (
        <>
          {report.outage_reason && (
            <Alert status="error" borderRadius="md" data-testid="db-health-outage">
              <AlertIcon />{report.outage_reason}
            </Alert>
          )}

          <Box px={4} py={3} borderRadius="md" borderWidth="2px" bg={`${verdict.scheme}.50`}
               borderColor={`${verdict.scheme}.400`} data-testid="db-health-verdict" data-tone={verdictKey}
               data-state={state} role="status">
            <HStack justify="space-between" align="center" spacing={4} flexWrap="wrap">
              <HStack spacing={3} flexWrap="wrap">
                <Badge colorScheme={verdict.scheme} fontSize="sm" px={2}>{verdict.label}</Badge>
                <Text fontSize="lg" fontWeight="bold" data-testid="db-health-headline">
                  {verdictHeadline(report, attention)}
                </Text>
              </HStack>
              {/* Two rings: what's failing now (health), and what could go wrong (risk). */}
              <HStack spacing={6} align="start" flexWrap="wrap">
                <ImpactDonut testId="db-health-pie" label="Database" items={rings.health}
                             nothingText="Nothing is failing now" />
                <RiskDonut testId="db-health-risk" label="Database risk" items={rings.risk} />
              </HStack>
            </HStack>
          </Box>

          {report.setup_note && (
            <Alert status="warning" borderRadius="md" data-testid="db-health-setup">
              <AlertIcon />{report.setup_note}
            </Alert>
          )}
        </>
      )}

      {report && (
        <>
          <EmailAlerts alerts={report.alerts} />

          {showsSupabase(report) && <SupabaseHealth supabase={report.supabase} />}

          <UsageMeters usage={report.usage} />
        </>
      )}

      {attention.length > 0 && (
        <VStack align="stretch" spacing={3} data-testid="db-health-attention">
          <Text fontWeight="bold">What to fix, most important first ({attention.length})</Text>
          <SizingNote sizing={sizing} testId="db-health-sizing" />
          {attention.map((r) => (r.kind === "check"
            ? <AttentionCard key={r.id} check={r.item} {...rowTask(r.item)} />
            : <AiFindingCard key={r.id} finding={r.item} impact={r.impact} task={taskByRef[r.id]}
                             canAssign={Boolean(me)} onAssign={setAssigningAi} onDismiss={found.askDismiss}
                             TaskChip={SourceTaskChip} />))}
        </VStack>
      )}

      {report && (
        <>
          {unknown.length > 0 && (
            <Box data-testid="db-health-unknown">
              <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>Couldn't check ({unknown.length})</Text>
              {unknown.map((c) => <CompactRow key={c.id} check={c} {...rowTask(c)} />)}
            </Box>
          )}

          {passed.length > 0 && (
            <Box data-testid="db-health-passed">
              <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>Passed ({passed.length})</Text>
              {passed.map((c) => <CompactRow key={c.id} check={c} {...rowTask(c)} />)}
            </Box>
          )}
        </>
      )}
      {loadFindings && (
        <Box borderWidth="1px" borderRadius="md" p={3}>
          <AiFindingsNote found={found} title="From the nightly code review" testId="db-health-ai"
                          blurb="Database problems Claude finds in each day's commits (migrations that lock big tables, queries without an index, row-level security and grant mistakes, code that uses tables or functions no migration creates) are in the list above with the checks', marked NIGHTLY AI REVIEW. Each stays until a commit fixes it or someone dismisses it." />
        </Box>
      )}
      {found.dismissDialog}
      {assigningAi && me && (() => {
        const { title, description } = aiFindingTaskText(assigningAi);
        return (
          <AssignModal isOpen onClose={() => setAssigningAi(null)} currentUser={me} taskOnly
                       initialTitle={title} initialDescription={description}
                       taskSource={{ source_kind: "db_health", source_ref: assigningAi.id }}
                       onAssigned={reloadTasks} />
        );
      })()}
      {assigning && me && (() => {
        const { title, description } = checkTaskText(assigning, report);
        return (
          <AssignModal
            isOpen
            onClose={() => setAssigning(null)}
            currentUser={me}
            taskOnly
            initialTitle={title}
            initialDescription={description}
            taskSource={{ source_kind: "db_health", source_ref: assigning.id }}
            onAssigned={reloadTasks}
          />
        );
      })()}
    </VStack>
  );
}
